import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useQuoteCustomFieldNames } from "@/store/quoteSettingsStore";
import { filterQuoteFieldOptions, isCustomFieldSource } from "./quoteFields";

interface FieldSlashMenuProps {
  /** Container-relative top of the menu. */
  top: number;
  /** Container-relative left of the menu. */
  left: number;
  /** The filter text typed after the "]" so far. */
  query: string;
  /** Inserts the chosen field at the caret. */
  onSelect: (source: string) => void;
  /** Dismisses the menu, keeping the typed "]" as literal text. */
  onDismiss: () => void;
}

/**
 * Floating field-insert menu shown at the caret while inline text editing:
 * typing "]" opens it, further typing filters the quote fields, ArrowUp/Down
 * move the highlight, Enter inserts the highlighted field, and Escape
 * dismisses (keeping the "]" as literal text). Only text-format fields are
 * offered (filterQuoteFieldOptions excludes markdown-format sources - those
 * insert as blocks via the palette). Keyboard events are captured
 * on window (capture phase) so the contenteditable block underneath never
 * sees them, and mouse picks preventDefault on mousedown so the editable
 * keeps focus (a blur would commit the edit).
 * @param top - Container-relative top of the menu
 * @param left - Container-relative left of the menu
 * @param query - The filter text typed after the "]"
 * @param onSelect - Inserts the chosen field at the caret
 * @param onDismiss - Dismisses the menu keeping the "]" literal
 */
export function FieldSlashMenu({
  top,
  left,
  query,
  onSelect,
  onDismiss,
}: FieldSlashMenuProps) {
  const customFieldNames = useQuoteCustomFieldNames();
  const options = useMemo(
    () => filterQuoteFieldOptions(query, customFieldNames),
    [query, customFieldNames],
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    itemRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, options]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        setActiveIndex((index) => Math.min(index + 1, options.length - 1));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        setActiveIndex((index) => Math.max(index - 1, 0));
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        const active = options[activeIndex];
        if (active) {
          onSelect(active.source);
        } else {
          onDismiss();
        }
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onDismiss();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [options, activeIndex, onSelect, onDismiss]);

  return (
    <div
      className="absolute z-50 w-60 max-h-64 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
      style={{ top, left }}
      role="listbox"
      aria-label="Insert field"
    >
      {options.length === 0 && (
        <p className="px-2 py-1.5 text-xs text-muted-foreground">
          No matching fields
        </p>
      )}
      {options.map((option, index) => (
        <button
          key={option.source}
          ref={(element) => {
            itemRefs.current[index] = element;
          }}
          type="button"
          role="option"
          aria-selected={index === activeIndex}
          className={cn(
            "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs",
            index === activeIndex && "bg-accent text-accent-foreground",
          )}
          onMouseDown={(event) => event.preventDefault()}
          onMouseEnter={() => setActiveIndex(index)}
          onClick={() => onSelect(option.source)}
        >
          <span>{option.label}</span>
          {isCustomFieldSource(option.source) && (
            <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
              Custom
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
